import { AxiosResponse } from 'axios'
import ApiService from '@/plugins/api-plugin/services/api.service'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import {
    CreatedImportJobDao,
    CreateImportJobDto,
    CreateRowsJobDto,
    ImportJobDao,
    ImportJobFileKind,
    ImportJobRowDao,
    ImportJobSummaryDao,
    ImportLimitsDao,
    ImportRevertDao,
    ImportRowOutcome,
    TemplateCountDao,
    TemplateSourceDto,
} from '@/interfaces/catalogue-imports/CatalogueImport.dao'

const JOB_FILE_PATHS: Record<ImportJobFileKind, string> = {
    file: 'file',
    result: 'result',
    rejected: 'result/rejected',
}

export interface ImportJobsQuery {
    clientId?: number
    page: number
    limit: number
}

export interface ImportJobRowsQuery {
    page: number
    limit: number
    problemsOnly?: boolean
    outcome?: ImportRowOutcome
    field?: string
}

/** The catalogue import engine (`/catalogue-imports`, role `Web_Nav_Inventory_MaterialsManagement`). */
export default class CatalogueImportsService extends ApiService {
    /** GET /catalogue-imports/templates/{operation} - the empty template of a profile, with the client header. */
    async downloadTemplate(operation: string, clientId: number): Promise<AxiosResponse<Blob> | null> {
        return this.request<undefined, AxiosResponse<Blob>>(
            'GET',
            `/catalogue-imports/templates/${encodeURIComponent(operation)}`,
            { clientId },
            undefined,
            'blob',
            'downloadCatalogueImportTemplate',
        ).catch(() => null)
    }

    /** POST /catalogue-imports/export/count - the records a template with rows of this source holds. */
    async countTemplate(source: TemplateSourceDto): Promise<TemplateCountDao | null> {
        return this.request<TemplateSourceDto, TemplateCountDao>(
            'POST',
            '/catalogue-imports/export/count',
            undefined,
            source,
            undefined,
            'countCatalogueImportTemplate',
        ).catch(() => null)
    }

    /** POST /catalogue-imports/export - the template of a profile filled with the rows of the source. */
    async exportTemplate(source: TemplateSourceDto): Promise<AxiosResponse<Blob> | null> {
        return this.request<TemplateSourceDto, AxiosResponse<Blob>>(
            'POST',
            '/catalogue-imports/export',
            undefined,
            source,
            'blob',
            'exportCatalogueImportTemplate',
        ).catch(() => null)
    }

    /** POST /catalogue-imports - uploads an .xlsx file as a new job; the job runs after the 202. */
    async createJob(payload: CreateImportJobDto): Promise<CreatedImportJobDao | null> {
        const form = new FormData()
        form.append('file', payload.file)
        form.append('clientId', String(payload.clientId))
        form.append('operation', payload.operation)
        form.append('mode', payload.mode)
        return this.request<FormData, CreatedImportJobDao>(
            'POST',
            '/catalogue-imports',
            undefined,
            form,
            undefined,
            'createCatalogueImportJob',
        ).catch(() => null)
    }

    /**
     * POST /catalogue-imports/rows - rows from a screen as one job. With `wait` an all-or-nothing
     * job of up to 200 rows runs in the request and comes back finished; the key replays the job a
     * repeated request first created. A refusal rejects with the server message.
     */
    async createRowsJob(dto: CreateRowsJobDto, idempotencyKey: string, wait: boolean): Promise<ImportJobDao> {
        return this.request<CreateRowsJobDto, ImportJobDao>(
            'POST',
            '/catalogue-imports/rows',
            wait ? { wait: true } : undefined,
            dto,
            undefined,
            'createCatalogueImportRowsJob',
            { suppressErrorAlert: true, headers: { 'Idempotency-Key': idempotencyKey } },
        )
    }

    /** POST /catalogue-imports/{id}/undo - the jobs that put back what the job changed. A refusal rejects with the server message. */
    async revertJob(id: number): Promise<ImportRevertDao> {
        return this.request<undefined, ImportRevertDao>(
            'POST',
            `/catalogue-imports/${id}/undo`,
            undefined,
            undefined,
            undefined,
            'revertCatalogueImportJob',
            { suppressErrorAlert: true },
        )
    }

    /** GET /catalogue-imports - the import journal of a client, or of every client in scope, newest first. */
    async getJobs(query: ImportJobsQuery): Promise<[ImportJobDao[], PageNumberPagination] | null> {
        return this.request<undefined, [ImportJobDao[], PageNumberPagination]>(
            'GET',
            '/catalogue-imports',
            { ...query },
            undefined,
            undefined,
            'getCatalogueImportJobs',
        ).catch(() => null)
    }

    async getJob(id: number): Promise<ImportJobDao | null> {
        return this.request<undefined, ImportJobDao>(
            'GET',
            `/catalogue-imports/${id}`,
            undefined,
            undefined,
            undefined,
            'getCatalogueImportJob',
        ).catch(() => null)
    }

    /** GET /catalogue-imports/{id}/summary - outcomes and changes per field; for a dry run, what would change. */
    async getJobSummary(id: number): Promise<ImportJobSummaryDao | null> {
        return this.request<undefined, ImportJobSummaryDao>(
            'GET',
            `/catalogue-imports/${id}/summary`,
            undefined,
            undefined,
            undefined,
            'getCatalogueImportJobSummary',
        ).catch(() => null)
    }

    /** GET /catalogue-imports/limits - the all-or-nothing row threshold and the upload limits. */
    async getLimits(): Promise<ImportLimitsDao | null> {
        return this.request<undefined, ImportLimitsDao>(
            'GET',
            '/catalogue-imports/limits',
            undefined,
            undefined,
            undefined,
            'getCatalogueImportLimits',
        ).catch(() => null)
    }

    /** GET /catalogue-imports/{id}/rows - row outcomes in row order, at most 100 per page. */
    async getJobRows(id: number, query: ImportJobRowsQuery): Promise<[ImportJobRowDao[], PageNumberPagination] | null> {
        return this.request<undefined, [ImportJobRowDao[], PageNumberPagination]>(
            'GET',
            `/catalogue-imports/${id}/rows`,
            { ...query },
            undefined,
            undefined,
            'getCatalogueImportJobRows',
        ).catch(() => null)
    }

    /** GET /catalogue-imports/{id}/file, /result or /result/rejected (the rejected rows of the result file). */
    async downloadJobFile(id: number, file: ImportJobFileKind): Promise<AxiosResponse<Blob> | null> {
        return this.request<undefined, AxiosResponse<Blob>>(
            'GET',
            `/catalogue-imports/${id}/${JOB_FILE_PATHS[file]}`,
            undefined,
            undefined,
            'blob',
            'downloadCatalogueImportJobFile',
        ).catch(() => null)
    }
}
